import { Box, Container, Grid, Typography, Select, MenuItem, Paper } from "@mui/material";
import { useTheme } from "@mui/material/styles";
import useMediaQuery from "@mui/material/useMediaQuery";
import { useMaterialUIController, setMiniSidenav } from "context";
import SetupCycle from "./setupcycle";
import CaptureRecords from "./capturerecords";
import React, { useCallback, useEffect, useRef, useState } from "react";
import axios from "axios";
import { farmsForUserUrl, broilersByFarmUrl, cyclesByFacilityUrl } from "api/endpoints";
import DashboardLayout from "examples/LayoutContainers/DashboardLayout";
import DashboardNavbar from "examples/Navbars/DashboardNavbar";

const STORAGE_KEY = "facilityLandingSelections";
const DROPDOWN_CACHE_KEY = "facilityLandingDropdownCache";
export default function FacilityLanding() {
  const [controller, dispatch] = useMaterialUIController();
  const [farms, setFarms] = useState([]);
  const [facilities, setFacilities] = useState([]);
  const requestVersion = useRef(0);
  const [filteredCycles, setFilteredCycles] = useState([]); // cycles for selected facility
  const [farm, setFarm] = useState("");
  const [facility, setFacility] = useState("");
  const [cycle, setCycle] = useState("");

  const persistSelection = useCallback((farmId, facilityId, cycleId) => {
    try {
      localStorage.setItem(
        STORAGE_KEY,
        JSON.stringify({
          farmId: farmId ?? null,
          facilityId: facilityId ?? null,
          cycleId: cycleId ?? null,
        })
      );
    } catch {}
  }, []);

  const readStoredSelection = useCallback(() => {
    try {
      const raw = localStorage.getItem(STORAGE_KEY);
      if (!raw) return null;
      return JSON.parse(raw);
    } catch {
      return null;
    }
  }, []);
  const theme = useTheme();
  // Show navbar on screens where sidenav is mini/hidden (< 1200px)
  const isTabletOrMobile = useMediaQuery(theme.breakpoints.down("lg"));
  useEffect(() => {
    try {
      // Collapse sidenav by default on this page (all sizes)
      setMiniSidenav(dispatch, true);
    } catch {}
  }, [dispatch]);
  const readCache = () => {
    try {
      return JSON.parse(localStorage.getItem(DROPDOWN_CACHE_KEY)) || {};
    } catch {
      return {};
    }
  };
  const cacheRows = (key, rows, parentKey, parentId) => {
    try {
      const cache = readCache();
      const previous = Array.isArray(cache[key]) ? cache[key] : [];
      cache[key] = parentKey
        ? [...previous.filter((row) => `${row[parentKey]}` !== `${parentId}`), ...rows]
        : rows;
      localStorage.setItem(DROPDOWN_CACHE_KEY, JSON.stringify(cache));
    } catch {}
  };
  const pick = (...values) => values.find((v) => v != null && `${v}` !== "");
  const loadRows = async (url, key, normalize, parentKey, parentId) => {
    try {
      if (navigator.onLine === false) throw new Error("Offline");
      const response = await axios.get(url);
      const data = Array.isArray(response.data) ? response.data : [];
      const rows = data.map(normalize).filter((row) => row.id != null && row.name);
      cacheRows(key, rows, parentKey, parentId);
      return rows;
    } catch (error) {
      console.error(`Failed to load ${key}`, error);
      const cached = readCache()[key];
      return Array.isArray(cached)
        ? cached.filter((row) => !parentKey || `${row[parentKey]}` === `${parentId}`)
        : [];
    }
  };
  const loadFacilities = (farmId) =>
    loadRows(
      broilersByFarmUrl(farmId),
      "facilities",
      (row) => ({
        id: pick(row.id, row.ID, row.broilerID, row.broilerId, row.facilityId),
        name: pick(row.name, row.broilerName, row.facilityName),
        farmId,
        raw: row,
      }),
      "farmId",
      farmId
    );

  const selectFacility = async (selectedFarm, selected, version, stored, latest = false) => {
    setFacility(selected?.name || "");
    setFilteredCycles([]);
    setCycle("");
    persistSelection(selectedFarm?.id, selected?.id, null);
    if (!selected) return;
    const cycles = await loadRows(
      cyclesByFacilityUrl(selected.id),
      "cycles",
      (row) => ({
        id: pick(row.id, row.ID, row.cycleId, row.cycleID),
        name: pick(row.name, row.cycleName, row.CycleName, row.cycle, row.Cycle),
        broilerId: selected.id,
        raw: row,
      }),
      "broilerId",
      selected.id
    );
    if (requestVersion.current !== version) return;
    const next =
      cycles.find((row) => `${row.id}` === `${stored?.cycleId}`) ||
      (latest ? cycles[cycles.length - 1] : cycles[0]);
    setFilteredCycles(cycles);
    setCycle(next?.name || "");
    persistSelection(selectedFarm.id, selected.id, next?.id);
  };

  const selectFarm = async (selected, version, stored, requestedFacilityId, preloaded) => {
    setFarm(selected?.name || "");
    setFacilities([]);
    setFacility("");
    setFilteredCycles([]);
    setCycle("");
    persistSelection(selected?.id, null, null);
    if (!selected) return;
    const rows = preloaded || (await loadFacilities(selected.id));
    if (requestVersion.current !== version) return;
    setFacilities(rows);
    const next =
      rows.find((row) => `${row.id}` === `${requestedFacilityId || stored?.facilityId}`) || rows[0];
    await selectFacility(selected, next, version, stored, Boolean(requestedFacilityId));
  };

  useEffect(() => {
    const version = ++requestVersion.current;
    const initialize = async () => {
      const stored = readStoredSelection();
      let session;
      try {
        session = JSON.parse(localStorage.getItem("OmnioLogin"));
      } catch {}
      const rows = session?.ID
        ? await loadRows(farmsForUserUrl(session.ID), "farms", (row) => ({
            id: pick(row.id, row.ID, row.farmId, row.FarmID, row.farmID),
            name: pick(row.name, row.farmName, row.FarmName),
            raw: row,
          }))
        : readCache().farms || [];
      if (requestVersion.current !== version) return;
      setFarms(rows);
      const requestedFacilityId = new URLSearchParams(window.location.search).get("facility");
      if (requestedFacilityId) {
        // A facility deep link does not include its farm, so resolve it within the user's farms.
        for (const candidate of rows) {
          const facilitiesForFarm = await loadFacilities(candidate.id);
          if (requestVersion.current !== version) return;
          if (facilitiesForFarm.some((row) => `${row.id}` === requestedFacilityId)) {
            await selectFarm(candidate, version, null, requestedFacilityId, facilitiesForFarm);
            return;
          }
        }
      }
      const selected = rows.find((row) => `${row.id}` === `${stored?.farmId}`) || rows[0];
      await selectFarm(selected, version, stored);
    };
    initialize();
    return () => {
      requestVersion.current += 1;
    };
    // Initialization runs once; event handlers load subsequent dependent selections.
  }, []);

  const handleFarmChange = (event) => {
    const selected = farms.find((row) => row.name === event.target.value);
    selectFarm(selected, ++requestVersion.current);
  };

  const handleFacilityChange = (event) => {
    const selected = facilities.find((row) => row.name === event.target.value);
    const currentFarm = farms.find((row) => row.name === farm);
    selectFacility(currentFarm, selected, ++requestVersion.current);
  };

  const handleCycleChange = (event) => {
    const selectedName = event.target.value;
    setCycle(selectedName);
    const currentFarm = farms.find((f) => f.name === farm);
    const currentFacility = facilities.find((f) => f.name === facility);
    const selectedCycle = filteredCycles.find((c) => c.name === selectedName);
    persistSelection(
      currentFarm?.id ?? null,
      currentFacility?.id ?? null,
      selectedCycle?.id ?? null
    );
  };

  const selectedFacility = facilities.find((f) => f.name === facility);
  const selectedCycle = filteredCycles.find((c) => c.name === cycle);
  const selectedFarm = farms.find((f) => f.name === farm);
  const selectedFarmId = selectedFarm?.id ?? selectedFacility?.farmId ?? null;
  const selectedFacilityId = selectedFacility?.id ?? null;
  const selectedCycleId = selectedCycle?.id ?? null;

  return (
    <DashboardLayout>
      <DashboardNavbar />
      <Box
        sx={{
          display: "flex",
          flexDirection: "column",
          px: { xs: 2, sm: 3, md: 4 },
          width: { xs: "100%", md: "90%" },
          pr: { xs: 0, md: 2 },
          transition: "all 0.3s ease",
        }}
      >
        <Container maxWidth="xl">
          {/* Dropdown selectors */}
          <Grid container spacing={2} mb={4}>
            <Grid item xs={12} sm={4}>
              <Typography>Farm:</Typography>
              <Select
                fullWidth
                value={farm}
                onChange={handleFarmChange}
                sx={{
                  height: "1cm",
                  "& .MuiOutlinedInput-root, & .MuiInputBase-root": { height: "1cm !important" },
                  "& .MuiSelect-select": {
                    minHeight: "1cm !important",
                    lineHeight: "1cm !important",
                    display: "flex",
                    alignItems: "center",
                    py: 0,
                  },
                }}
              >
                {farms.map((f) => (
                  <MenuItem key={f.id || f.name} value={f.name}>
                    {f.name}
                  </MenuItem>
                ))}
              </Select>
            </Grid>
            <Grid item xs={12} sm={4}>
              <Typography>Facility:</Typography>
              <Select
                fullWidth
                value={facility}
                onChange={handleFacilityChange}
                sx={{
                  height: "1cm",
                  "& .MuiOutlinedInput-root, & .MuiInputBase-root": { height: "1cm !important" },
                  "& .MuiSelect-select": {
                    minHeight: "1cm !important",
                    lineHeight: "1cm !important",
                    display: "flex",
                    alignItems: "center",
                    py: 0,
                  },
                }}
              >
                {facilities.map((fac) => (
                  <MenuItem key={fac.id || fac.name} value={fac.name}>
                    {fac.name}
                  </MenuItem>
                ))}
              </Select>
            </Grid>
            <Grid item xs={12} sm={4}>
              <Typography>Cycle:</Typography>
              <Select
                fullWidth
                value={cycle}
                onChange={handleCycleChange}
                sx={{
                  height: "1cm",
                  "& .MuiOutlinedInput-root, & .MuiInputBase-root": { height: "1cm !important" },
                  "& .MuiSelect-select": {
                    minHeight: "1cm !important",
                    lineHeight: "1cm !important",
                    display: "flex",
                    alignItems: "center",
                    py: 0,
                  },
                }}
              >
                {filteredCycles.length ? (
                  filteredCycles.map((c) => (
                    <MenuItem key={c.id || c.name} value={c.name}>
                      {c.name}
                    </MenuItem>
                  ))
                ) : (
                  <MenuItem value="">No cycles</MenuItem>
                )}
              </Select>
            </Grid>
          </Grid>

          {/* Action panels */}
          <Grid container spacing={6}>
            <Grid item xs={12} md={6}>
              <Paper sx={{ p: 3 }}>
                <Typography variant="h6" gutterBottom>
                  Start Cycle
                </Typography>
                <SetupCycle
                  facilityId={selectedFacilityId}
                  cycleId={selectedCycleId}
                  farmId={selectedFarmId}
                />
              </Paper>
            </Grid>
            <Grid item xs={12} md={6}>
              <Paper sx={{ p: 3 }}>
                <Typography variant="h6" gutterBottom>
                  Daily/Weekly Records
                </Typography>
                <CaptureRecords
                  facilityId={selectedFacilityId}
                  cycleId={selectedCycleId}
                  farmId={selectedFarmId}
                />
              </Paper>
            </Grid>
          </Grid>
        </Container>
      </Box>
    </DashboardLayout>
  );
}
